<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div v-if="!canManageAppointments" class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="alert alert-warning mb-0">
              You do not have permission to review appointment schedules for this business.
            </div>
          </div>
        </div>
      </div>
    </div>

    <template v-else>
      <div class="row">
        <div class="col-12">
          <b-tabs v-model="activeScheduleTab" pills nav-class="p-0 schedule-center-tabs mb-3" content-class="text-dark">
            <b-tab title="Availability Review" class="border-0"></b-tab>
            <b-tab title="Schedule Calendar" class="border-0"></b-tab>
          </b-tabs>
        </div>

        <div v-if="activeScheduleTab === 0" class="col-12">
          <div class="card schedule-review-card">
            <div class="card-body">
              <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                <div>
                  <h4 class="card-title mb-1">Availability Review</h4>
                  <p class="text-muted mb-0">Approve, reject, and create slots from doctor availability.</p>
                </div>
                <div class="d-flex align-items-center gap-2 flex-wrap">
                  <span class="badge bg-soft-warning text-warning pending-pill">Pending {{ pendingCount }}</span>
                  <multiselect
                    v-model="serviceBatchFilter"
                    :options="serviceFilterOptions"
                    label="label"
                    track-by="value"
                    :searchable="true"
                    :close-on-select="true"
                    :allow-empty="true"
                    :show-labels="false"
                    placeholder="Filter by service"
                    class="service-filter-select"
                  ></multiselect>
                  <b-button variant="success" size="sm" :disabled="!filteredPendingIds.length || approvingBatch" @click="approveAvailabilityIds(filteredPendingIds)">
                    <b-spinner v-if="approvingBatch" small class="align-middle me-1"></b-spinner>
                    Approve Filtered Pending
                  </b-button>
                </div>
              </div>

              <div v-if="loading" class="review-loading">
                <b-spinner class="align-middle me-1" small></b-spinner>
                <strong>Loading schedule review data...</strong>
              </div>

              <div v-else-if="!groupRows.length" class="review-empty">
                No appointment availability has been submitted yet.
              </div>

              <div v-else class="review-table-shell">
                <table class="review-table">
                  <thead>
                    <tr>
                      <th>Submitter</th>
                      <th>Pending Submissions</th>
                      <th>Rows</th>
                      <th>Approved</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="group in paginatedGroupRows">
                      <tr
                        :key="`group-${group.id}`"
                        class="review-group-row"
                        :class="{ open: expandedGroupId === group.id }"
                        @click="toggleGroup(group.id)"
                      >
                        <td>
                          <div class="review-submitter">
                            <strong class="review-link-text">{{ group.doctor_name }}</strong>
                          </div>
                        </td>
                        <td>
                          <span class="badge" :class="group.pending_count ? 'bg-soft-warning text-warning' : 'bg-soft-secondary text-secondary'">
                            {{ group.pending_count }}
                          </span>
                        </td>
                        <td>{{ group.total_count }}</td>
                        <td>{{ group.approved_count }}</td>
                        <td @click.stop>
                          <div class="review-actions">
                            <b-button
                              v-if="group.pending_count"
                              size="sm"
                              variant="success"
                              @click="promptGroupApproval(group)"
                            >
                              Approve All
                            </b-button>
                            <b-button
                              v-if="group.pending_count"
                              size="sm"
                              variant="danger"
                              @click="openRejectModal(group.pending_ids, group.doctor_name)"
                            >
                              Reject All
                            </b-button>
                            <b-button
                              v-if="!group.pending_count && group.approved_availabilities.length"
                              size="sm"
                              variant="primary"
                              @click="promptGroupSlotCreation(group)"
                            >
                              Create Slots
                            </b-button>
                            <b-button size="sm" variant="outline-primary" @click="toggleGroup(group.id)">
                              {{ expandedGroupId === group.id ? "Hide" : "View" }}
                            </b-button>
                            <button
                              v-if="!group.pending_count"
                              type="button"
                              class="icon-action-button"
                              @click="promptDelete(group.availability_ids, `${group.doctor_name}'s availability`, 'group')"
                            >
                              <i class="mdi mdi-delete-outline"></i>
                            </button>
                          </div>
                        </td>
                      </tr>

                      <tr v-if="expandedGroupId === group.id" :key="`details-${group.id}`" class="review-slide-row">
                        <td colspan="5" class="review-slide-cell">
                          <transition name="review-expand" appear>
                            <div class="review-group-details">
                              <table class="review-detail-table">
                                <thead>
                                  <tr>
                                    <th>Service</th>
                                    <th>Type</th>
                                    <th>Period</th>
                                    <th>Status</th>
                                    <th>Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr
                                    v-for="availability in group.availabilities"
                                    :key="availability.id"
                                    class="review-detail-row"
                                    @click="openAvailabilityDetail(availability)"
                                  >
                                    <td>
                                      <div class="review-service-cell">
                                        <strong class="review-link-text">{{ availability.service_name }}</strong>
                                        <span>{{ availability.service_category_name || "No category" }}</span>
                                      </div>
                                    </td>
                                    <td>{{ formatAvailabilityType(availability) }}</td>
                                    <td>{{ formatAvailabilityWindow(availability) }}</td>
                                    <td>
                                      <span class="badge" :class="statusBadgeClass(availability.status)">{{ formatStatus(availability.status) }}</span>
                                    </td>
                                    <td @click.stop>
                                      <div class="review-actions review-actions-inline">
                                        <b-button size="sm" variant="outline-primary" @click="openAvailabilityDetail(availability)">View</b-button>
                                        <b-button v-if="availability.status === 'pending_review'" size="sm" variant="success" @click="promptSingleApproval(availability)">Approve</b-button>
                                        <b-button v-if="availability.status === 'pending_review'" size="sm" variant="danger" @click="openRejectModal([availability.id], availability.doctor_name)">Reject</b-button>
                                        <b-button v-if="availability.status === 'approved'" size="sm" variant="primary" @click="openSlotModal(availability)">Create Slots</b-button>
                                        <button
                                          v-if="availability.status !== 'pending_review'"
                                          type="button"
                                          class="icon-action-button"
                                          @click="promptDelete([availability.id], availability.service_name || 'availability', 'single')"
                                        >
                                          <i class="mdi mdi-delete-outline"></i>
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </transition>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>

              <div v-if="groupRows.length > perPage" class="review-pagination">
                <b-button size="sm" variant="light" :disabled="currentPage === 1" @click="currentPage -= 1">Previous</b-button>
                <span>Page {{ currentPage }} of {{ totalPages }}</span>
                <b-button size="sm" variant="light" :disabled="currentPage === totalPages" @click="currentPage += 1">Next</b-button>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="col-12">
          <div class="card calendar-shell-card">
            <div class="card-body">
              <div class="d-flex align-items-center justify-content-between mb-3 gap-2 flex-wrap">
                <div>
                  <h4 class="card-title mb-1">Schedule Calendar</h4>
                  <p class="text-muted mb-0">This calendar shows hospital-wide appointment slots across the business.</p>
                </div>
                <div class="calendar-controls">
                  <b-button size="sm" variant="light" @click="shiftMonth(-1)">
                    <i class="mdi mdi-chevron-left"></i>
                  </b-button>
                  <span class="fw-medium">{{ calendarLabel }}</span>
                  <b-button size="sm" variant="light" @click="shiftMonth(1)">
                    <i class="mdi mdi-chevron-right"></i>
                  </b-button>
                </div>
              </div>

              <div class="calendar-shell">
                <div class="calendar-pane">
                  <div class="calendar-grid">
                    <div v-for="weekday in weekDays" :key="weekday" class="calendar-head">{{ weekday }}</div>
                    <button
                      v-for="day in calendarCells"
                      :key="day.key"
                      type="button"
                      class="calendar-cell"
                      :class="{ muted: !day.inMonth, active: selectedCalendarDate === day.date, today: day.date === todayIso, touched: day.count > 0, booked: day.booked > 0 }"
                      @click="selectCalendarDate(day.date)"
                    >
                      <span class="day-number">{{ day.day }}</span>
                      <span v-if="day.count" class="calendar-count">{{ day.count }}</span>
                      <span v-if="day.booked" class="calendar-booked-dot"></span>
                    </button>
                  </div>
                </div>

                <div class="slots-pane">
                  <div class="slots-pane-head">
                    <div>
                      <h5 class="mb-1">Day Schedule</h5>
                      <small class="text-muted">{{ selectedCalendarDateLabel }}</small>
                    </div>
                    <span v-if="selectedDayBookedCount" class="badge bg-soft-danger text-danger">
                      {{ selectedDayBookedCount }} booked
                    </span>
                  </div>

                  <div v-if="loadingDaySlots" class="slots-state">
                    <b-spinner small class="align-middle me-1"></b-spinner>
                    <strong>Loading day schedule...</strong>
                  </div>

                  <div v-else-if="!daySlots.length" class="slots-state empty">
                    No hospital slots are available for this date yet.
                  </div>

                  <div v-else class="slot-list">
                    <div
                      v-for="slot in daySlots"
                      :id="slotPopoverTarget(slot)"
                      :key="slot.id"
                      class="slot-card"
                      :class="{ 'slot-card--booked': slot.status === 'booked' }"
                    >
                      <div class="d-flex justify-content-between align-items-start gap-2">
                        <div>
                          <strong>{{ slot.service_name }}</strong>
                          <div class="small text-muted">{{ slot.doctor_name }}</div>
                        </div>
                        <span class="badge" :class="slot.status === 'booked' ? 'bg-soft-danger text-danger' : 'bg-soft-success text-success'">
                          {{ formatStatus(slot.status) }}
                        </span>
                      </div>
                      <div class="small text-muted mt-2">{{ shortTime(slot.start_time) }} - {{ shortTime(slot.end_time) }}</div>
                      <div v-if="slot.status === 'booked'" class="slot-card-foot mt-2">
                        <span class="slot-booking-pill">
                          <i class="mdi mdi-account-clock-outline me-1"></i>
                          Hover to view booking details
                        </span>
                      </div>
                    </div>

                    <b-popover
                      v-for="slot in bookedDaySlots"
                      :key="`slot-popover-${slot.id}`"
                      :target="slotPopoverTarget(slot)"
                      triggers="hover focus"
                      placement="auto"
                      boundary="viewport"
                      custom-class="slot-booking-popover"
                    >
                      <div class="slot-booking-popover-body">
                        <div class="slot-booking-popover-head">
                          <span class="slot-booking-popover-kicker">Booked Slot</span>
                          <strong>{{ shortTime(slot.start_time) }} - {{ shortTime(slot.end_time) }}</strong>
                        </div>
                        <div class="slot-booking-popover-service">{{ slot.service_name }}</div>

                        <div v-if="slotBookingDetails(slot).length" class="slot-booking-popover-grid">
                          <div v-for="detail in slotBookingDetails(slot)" :key="detail.label" class="slot-booking-popover-row">
                            <span class="slot-booking-popover-label">{{ detail.label }}</span>
                            <strong class="slot-booking-popover-value">{{ detail.value }}</strong>
                          </div>
                        </div>

                        <div v-else class="slot-booking-popover-empty">
                          Booking details are not available for this slot yet.
                        </div>
                      </div>
                    </b-popover>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <b-modal id="reject-availability-modal" centered hide-footer title="Reject Availability" modal-class="schedule-modal-shell">
      <form @submit.prevent="rejectSelected">
        <div class="mb-3">
          <p class="mb-2">Reject availability for <strong>{{ rejectionTargetLabel }}</strong>.</p>
          <label class="form-label">Reason</label>
          <b-form-textarea v-model="rejectionReason" rows="4" placeholder="State the reason for rejecting this availability"></b-form-textarea>
        </div>
        <div class="d-flex justify-content-end gap-2">
          <b-button type="button" variant="secondary" @click="$bvModal.hide('reject-availability-modal')">Cancel</b-button>
          <b-button type="submit" variant="danger" :disabled="rejecting">
            <b-spinner v-if="rejecting" small class="align-middle me-1"></b-spinner>
            {{ rejecting ? "Rejecting..." : "Reject Availability" }}
          </b-button>
        </div>
      </form>
    </b-modal>

    <b-modal id="create-slots-modal" centered size="lg" hide-footer title="Create Slots" modal-class="schedule-modal-shell">
      <form @submit.prevent="saveSlots">
        <div class="mb-3">
          <h5 class="mb-1">{{ slotAvailability.service_name }}</h5>
          <p class="text-muted mb-0">{{ formatAvailabilityWindow(slotAvailability) }}</p>
        </div>

        <div class="row">
          <div class="col-md-12 mb-3">
            <label class="form-label">Break Windows</label>
            <div class="slot-breaks">
              <span v-if="!slotAvailability.breaks || !slotAvailability.breaks.length" class="text-muted small">None configured</span>
              <div v-else class="small text-muted">
                <div v-for="item in slotAvailability.breaks" :key="item.id">
                  {{ shortTime(item.break_start_time) }} - {{ shortTime(item.break_end_time) }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="auto-slot-summary">
          <div class="summary-item">
            <div class="summary-label">Slot Duration</div>
            <div class="summary-value">{{ slotAvailability.default_slot_duration_minutes || 0 }} minutes</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Slot Price</div>
            <div class="summary-value">{{ money(slotAvailability.default_price) }}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Slots To Create</div>
            <div class="summary-value">{{ autoSlotCount }}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Target Days</div>
            <div class="summary-value">{{ slotGenerationDays }}</div>
          </div>
        </div>

        <div class="alert alert-info mt-3 mb-0">
          The system will automatically create every valid slot within this availability window using the configured service duration, while respecting all break windows and avoiding overlaps.
        </div>

        <div class="d-flex justify-content-end gap-2 mt-3">
          <b-button type="button" variant="secondary" @click="$bvModal.hide('create-slots-modal')">Close</b-button>
          <b-button type="submit" variant="primary" :disabled="savingSlots || !autoSlotCount">
            <b-spinner v-if="savingSlots" small class="align-middle me-1"></b-spinner>
            {{ savingSlots ? "Creating..." : "Create Slots" }}
          </b-button>
        </div>
      </form>
    </b-modal>

    <b-modal
      id="availability-detail-modal"
      centered
      size="lg"
      title="Availability Details"
      modal-class="schedule-modal-shell availability-detail-shell"
    >
      <div v-if="detailAvailability.id" class="availability-detail-modal">
        <div class="availability-detail-hero">
          <div>
            <span class="detail-eyebrow">{{ detailAvailability.doctor_name || detailAvailability.submitted_by_name || "Availability" }}</span>
            <h4 class="mb-1">{{ detailAvailability.service_name }}</h4>
            <p class="text-muted mb-0">{{ detailAvailability.service_category_name || "No category assigned" }}</p>
          </div>
          <span class="badge" :class="statusBadgeClass(detailAvailability.status)">{{ formatStatus(detailAvailability.status) }}</span>
        </div>

        <div class="availability-detail-grid">
          <div class="detail-stat">
            <div class="detail-label">Type</div>
            <div class="detail-value">{{ formatAvailabilityType(detailAvailability) }}</div>
          </div>
          <div class="detail-stat">
            <div class="detail-label">Period</div>
            <div class="detail-value">{{ formatAvailabilityWindow(detailAvailability) }}</div>
          </div>
          <div class="detail-stat">
            <div class="detail-label">Submitted</div>
            <div class="detail-value">{{ formatDateTime(detailAvailability.created_at) || "Not available" }}</div>
          </div>
          <div class="detail-stat">
            <div class="detail-label">Slots</div>
            <div class="detail-value">{{ detailAvailability.slots_count || 0 }}</div>
          </div>
        </div>

        <div class="detail-section">
          <h6>Submission Details</h6>
          <div class="detail-copy">
            <div><strong>Submitted by:</strong> {{ detailAvailability.submitted_by_name || detailAvailability.doctor_name || "Unknown" }}</div>
            <div v-if="detailAvailability.approved_at"><strong>Approved:</strong> {{ formatDateTime(detailAvailability.approved_at) }} by {{ detailAvailability.approved_by_name || "Manager" }}</div>
            <div v-if="detailAvailability.rejected_at"><strong>Rejected:</strong> {{ formatDateTime(detailAvailability.rejected_at) }} by {{ detailAvailability.rejected_by_name || "Manager" }}</div>
            <div v-if="detailAvailability.withdrawn_at"><strong>Withdrawn:</strong> {{ formatDateTime(detailAvailability.withdrawn_at) }}</div>
          </div>
        </div>

        <div class="detail-section">
          <h6>Break Windows</h6>
          <div v-if="detailAvailability.breaks && detailAvailability.breaks.length" class="detail-breaks">
            <span v-for="item in detailAvailability.breaks" :key="item.id || `${item.break_start_time}-${item.break_end_time}`" class="detail-break-pill">
              {{ formatTime(item.break_start_time) }} - {{ formatTime(item.break_end_time) }}
            </span>
          </div>
          <p v-else class="text-muted mb-0">No break windows were added.</p>
        </div>

        <div v-if="detailAvailability.notes" class="detail-section">
          <h6>Doctor Notes</h6>
          <p class="detail-note mb-0">{{ detailAvailability.notes }}</p>
        </div>

        <div v-if="detailAvailability.rejection_reason" class="detail-section">
          <h6>Rejection Reason</h6>
          <p class="detail-note detail-note-danger mb-0">{{ detailAvailability.rejection_reason }}</p>
        </div>
      </div>

      <template #modal-footer>
        <div class="availability-detail-footer">
          <b-button type="button" variant="secondary" @click="$bvModal.hide('availability-detail-modal')">Close</b-button>
          <b-button v-if="detailAvailability.status === 'approved'" type="button" variant="primary" @click="openSlotModal(detailAvailability)">
            Create Slots
          </b-button>
          <b-button v-if="detailAvailability.status === 'pending_review'" type="button" variant="danger" @click="openRejectModal([detailAvailability.id], detailAvailability.doctor_name)">
            Reject
          </b-button>
          <b-button v-if="detailAvailability.status === 'pending_review'" type="button" variant="success" @click="promptSingleApproval(detailAvailability)">
            Approve
          </b-button>
        </div>
      </template>
    </b-modal>

    <b-modal id="approve-availability-modal" centered hide-footer title="Approve Availability" modal-class="schedule-modal-shell">
      <div v-if="approvalDialog.ids.length">
        <template v-if="approvalDialog.step === 1">
          <p class="mb-2">Are you sure you want to approve {{ approvalDialog.label }}?</p>
          <div class="approve-choice-card">
            <strong>{{ approvalDialog.title }}</strong>
            <div class="small text-muted mt-1">{{ approvalDialog.description }}</div>
          </div>
          <div class="d-flex justify-content-end gap-2 mt-3 flex-wrap">
            <b-button type="button" variant="secondary" @click="$bvModal.hide('approve-availability-modal')">Cancel</b-button>
            <b-button type="button" variant="success" @click="approvalDialog.step = 2">Yes, Continue</b-button>
          </div>
        </template>
        <template v-else>
          <p class="mb-2">Should the system also create slots immediately after approval?</p>
          <div class="approve-choice-card">
            <strong>{{ approvalDialog.title }}</strong>
            <div class="small text-muted mt-1">{{ approvalDialog.slotDescription }}</div>
          </div>
          <div class="d-flex justify-content-end gap-2 mt-3 flex-wrap">
            <b-button type="button" variant="secondary" @click="$bvModal.hide('approve-availability-modal')">Cancel</b-button>
            <b-button type="button" variant="success" :disabled="approvingSingle" @click="approveSelected(false)">
              <b-spinner v-if="approvingSingle && !approveAndCreateSlots" small class="align-middle me-1"></b-spinner>
              Approve Only
            </b-button>
            <b-button type="button" variant="primary" :disabled="approvingSingle" @click="approveSelected(true)">
              <b-spinner v-if="approvingSingle && approveAndCreateSlots" small class="align-middle me-1"></b-spinner>
              Approve and Create Slots
            </b-button>
          </div>
        </template>
      </div>
    </b-modal>

    <b-modal id="bulk-create-slots-modal" centered hide-footer title="Create Slots" modal-class="schedule-modal-shell">
      <div v-if="bulkSlotDialog.availabilities.length">
        <p class="mb-2">This will create slots for all approved services under <strong>{{ bulkSlotDialog.label }}</strong>.</p>
        <div class="approve-choice-card">
          <strong>{{ bulkSlotDialog.availabilities.length }} approved availability rows</strong>
          <div class="small text-muted mt-1">When you continue, the system will generate slots for every approved service shown under this submitter.</div>
        </div>
        <div class="d-flex justify-content-end gap-2 mt-3 flex-wrap">
          <b-button type="button" variant="secondary" @click="$bvModal.hide('bulk-create-slots-modal')">Cancel</b-button>
          <b-button type="button" variant="primary" :disabled="bulkCreatingSlots" @click="handleBulkSlotCreation()">
            <b-spinner v-if="bulkCreatingSlots" small class="align-middle me-1"></b-spinner>
            {{ bulkCreatingSlots ? "Creating..." : "Create Slots" }}
          </b-button>
        </div>
      </div>
    </b-modal>

    <b-modal id="delete-availability-modal" centered hide-footer title="Delete Availability" modal-class="schedule-modal-shell">
      <div v-if="deleteDialog.ids.length">
        <p class="mb-2">Are you sure you want to delete <strong>{{ deleteDialog.label }}</strong>?</p>
        <div class="approve-choice-card">
          <strong>{{ deleteDialog.ids.length }} row<span v-if="deleteDialog.ids.length > 1">s</span></strong>
          <div class="small text-muted mt-1">This removes the selected availability rows and any unbooked slots linked to them.</div>
        </div>
        <div class="d-flex justify-content-end gap-2 mt-3 flex-wrap">
          <b-button type="button" variant="secondary" @click="$bvModal.hide('delete-availability-modal')">Cancel</b-button>
          <b-button type="button" variant="danger" :disabled="deletingAvailability" @click="deleteSelectedAvailabilities()">
            <b-spinner v-if="deletingAvailability" small class="align-middle me-1"></b-spinner>
            {{ deletingAvailability ? "Deleting..." : "Delete" }}
          </b-button>
        </div>
      </div>
    </b-modal>
  </Layout>
</template>

<script>
import axios from "axios";
import Multiselect from "vue-multiselect";
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";

export default {
  page: {
    title: "Schedule Center",
    meta: [{ name: "description", content: appConfig.description }],
  },
  components: { Layout, PageHeader, Multiselect },
  data() {
    return {
      title: "Schedule Center",
      items: [
        { text: "Dashboard", to: "/" },
        { text: "Schedule Center", active: true },
      ],
      userData: JSON.parse(localStorage.getItem("user")) || {},
      loading: false,
      loadingDaySlots: false,
      approvingBatch: false,
      rejecting: false,
      savingSlots: false,
      activeScheduleTab: 0,
      currentPage: 1,
      perPage: 8,
      availabilities: [],
      pendingCount: 0,
      calendarDays: [],
      daySlots: [],
      daySlotBookings: [],
      serviceBatchFilter: "",
      rejectionReason: "",
      rejectionIds: [],
      rejectionTargetLabel: "",
      expandedGroupId: null,
      detailAvailability: {},
      approvalDialog: {
        ids: [],
        availabilities: [],
        label: "",
        title: "",
        description: "",
        slotDescription: "",
        step: 1,
      },
      bulkSlotDialog: {
        label: "",
        availabilities: [],
      },
      deleteDialog: {
        ids: [],
        label: "",
      },
      approvingSingle: false,
      approveAndCreateSlots: false,
      bulkCreatingSlots: false,
      deletingAvailability: false,
      slotAvailability: {},
      currentMonth: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      selectedCalendarDate: "",
      weekDays: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    };
  },
  computed: {
    canManageAppointments() {
      return Number(this.userData?.permissions?.appointment_manager || 0) === 1;
    },
    todayIso() {
      return this.isoDate(new Date());
    },
    serviceFilterOptions() {
      const services = Array.from(new Set(this.availabilities.map((item) => item.service_name).filter(Boolean))).sort();
      return [{ value: "", label: "All services" }].concat(services.map((item) => ({ value: item, label: item })));
    },
    filteredAvailabilities() {
      const selectedService = this.serviceBatchFilter?.value || "";
      const visibleAvailabilities = this.availabilities.filter((item) => item.status !== "withdrawn");
      if (!selectedService) return visibleAvailabilities;
      return visibleAvailabilities.filter((item) => item.service_name === selectedService);
    },
    filteredPendingIds() {
      return this.filteredAvailabilities.filter((item) => item.status === "pending_review").map((item) => item.id);
    },
    groupRows() {
      const groups = {};
      this.filteredAvailabilities.forEach((item) => {
        if (!groups[item.doctor_user_id]) {
          groups[item.doctor_user_id] = {
            id: item.doctor_user_id,
            doctor_name: item.doctor_name || "Unknown doctor",
            doctor_contact: item.doctor_contact || "",
            availabilities: [],
          };
        }
        groups[item.doctor_user_id].availabilities.push(item);
      });
      return Object.values(groups).map((group) => {
        const sortedAvailabilities = group.availabilities.slice().sort((left, right) => {
          const leftPending = left.status === "pending_review" ? 0 : 1;
          const rightPending = right.status === "pending_review" ? 0 : 1;
          if (leftPending !== rightPending) return leftPending - rightPending;
          return new Date(right.created_at || 0) - new Date(left.created_at || 0);
        });
        const pendingIds = sortedAvailabilities.filter((item) => item.status === "pending_review").map((item) => item.id);
        const approvedAvailabilities = sortedAvailabilities.filter((item) => item.status === "approved");
        const approvedCount = approvedAvailabilities.length;
        return {
          ...group,
          availabilities: sortedAvailabilities,
          total_count: group.availabilities.length,
          pending_count: pendingIds.length,
          approved_count: approvedCount,
          pending_ids: pendingIds,
          approved_availabilities: approvedAvailabilities,
          availability_ids: sortedAvailabilities.map((item) => item.id),
          services: Array.from(new Set(group.availabilities.map((item) => item.service_name).filter(Boolean))).join(", "),
        };
      }).sort((left, right) => {
        if (right.pending_count !== left.pending_count) return right.pending_count - left.pending_count;
        return left.doctor_name.localeCompare(right.doctor_name);
      });
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.groupRows.length / this.perPage));
    },
    paginatedGroupRows() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.groupRows.slice(start, start + this.perPage);
    },
    calendarMap() {
      return this.calendarDays.reduce((acc, item) => {
        acc[item.slot_date] = item;
        return acc;
      }, {});
    },
    calendarLabel() {
      return this.currentMonth.toLocaleString(undefined, { month: "long", year: "numeric" });
    },
    selectedCalendarDateLabel() {
      if (!this.selectedCalendarDate) {
        return "Choose a calendar day";
      }
      return this.formatDate(this.selectedCalendarDate);
    },
    selectedDayBookedCount() {
      return this.daySlots.filter((slot) => slot.status === "booked").length;
    },
    bookedDaySlots() {
      return this.daySlots.filter((slot) => slot.status === "booked");
    },
    daySlotBookingsBySlotId() {
      return this.daySlotBookings.reduce((acc, booking) => {
        if (booking.appointment_slot_id) {
          acc[booking.appointment_slot_id] = booking;
        }
        return acc;
      }, {});
    },
    autoSlotCount() {
      if (!this.slotAvailability?.default_slot_duration_minutes) return 0;

      const duration = Number(this.slotAvailability.default_slot_duration_minutes || 0);
      if (duration <= 0) return 0;

      const availabilityStart = this.timeToMinutes(this.shortTime(this.slotAvailability.start_time));
      const availabilityEnd = this.timeToMinutes(this.shortTime(this.slotAvailability.end_time));

      if (availabilityEnd <= availabilityStart) return 0;

      const breaks = (this.slotAvailability.breaks || [])
        .map((item) => ({
          start: this.timeToMinutes(this.shortTime(item.break_start_time)),
          end: this.timeToMinutes(this.shortTime(item.break_end_time)),
        }))
        .sort((a, b) => a.start - b.start);

      let cursor = availabilityStart;
      let total = 0;

      breaks.forEach((item) => {
        if (cursor < item.start) {
          total += Math.floor((item.start - cursor) / duration);
        }
        cursor = Math.max(cursor, item.end);
      });

      if (cursor < availabilityEnd) {
        total += Math.floor((availabilityEnd - cursor) / duration);
      }

      return total;
    },
    slotGenerationDays() {
      if (!this.slotAvailability?.type) return 0;
      if (this.slotAvailability.type === "one_off") return 1;
      return this.recurringDateCount;
    },
    recurringDateCount() {
      if (this.slotAvailability?.type !== "recurring" || !this.slotAvailability.start_date) return 0;

      const startDate = new Date(this.slotAvailability.start_date);
      const endDate = this.slotAvailability.end_date ? new Date(this.slotAvailability.end_date) : new Date(this.slotAvailability.start_date);
      const weekday = String(this.slotAvailability.day_of_week || "").toLowerCase();
      let count = 0;

      for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
        if (date.toLocaleString("en-US", { weekday: "long" }).toLowerCase() === weekday) {
          count += 1;
        }
      }

      return count;
    },
    calendarCells() {
      const start = new Date(this.currentMonth);
      const firstDay = start.getDay();
      start.setDate(1 - firstDay);
      const cells = [];
      for (let index = 0; index < 42; index += 1) {
        const date = new Date(start);
        date.setDate(start.getDate() + index);
        const iso = this.isoDate(date);
        const daySummary = this.calendarMap[iso] || {};
        cells.push({
          key: `${iso}-${index}`,
          date: iso,
          day: date.getDate(),
          inMonth: date.getMonth() === this.currentMonth.getMonth(),
          count: Number(daySummary.total_slots || 0),
          booked: Number(daySummary.booked_slots || 0),
        });
      }
      return cells;
    },
  },
  mounted() {
    if (this.canManageAppointments) {
      this.initialLoad();
    }
  },
  watch: {
    serviceBatchFilter() {
      this.currentPage = 1;
    },
    groupRows() {
      if (this.currentPage > this.totalPages) {
        this.currentPage = this.totalPages;
      }
    },
  },
  methods: {
    async initialLoad() {
      this.loading = true;
      try {
        const response = await axios.get("/appointmentschedulecenter");
        this.availabilities = response.data?.availabilities || [];
        this.pendingCount = Number(response.data?.pending_count || 0);
        this.calendarDays = response.data?.calendar_days || [];
        if (!this.selectedCalendarDate) {
          await this.selectCalendarDate(this.todayIso);
        } else if (this.selectedCalendarDate) {
          await this.selectCalendarDate(this.selectedCalendarDate);
        }
      } catch (error) {
        this.showRequestError(error, "Failed to load appointment schedule center.");
      } finally {
        this.loading = false;
      }
    },
    async approveAvailabilityIds(ids) {
      if (!ids.length) return;
      this.approvingBatch = true;
      try {
        const response = await axios.post("/approveappointmentavailabilities", { ids });
        this.$toast.success(response.data?.message || "Availability approved successfully.");
        await this.initialLoad();
      } catch (error) {
        this.showRequestError(error, "Failed to approve availability.");
      } finally {
        this.approvingBatch = false;
      }
    },
    openRejectModal(ids, label) {
      this.rejectionIds = ids;
      this.rejectionTargetLabel = label;
      this.rejectionReason = "";
      this.$bvModal.show("reject-availability-modal");
    },
    toggleGroup(id) {
      this.expandedGroupId = this.expandedGroupId === id ? null : id;
    },
    openAvailabilityDetail(availability) {
      this.detailAvailability = availability || {};
      this.$bvModal.show("availability-detail-modal");
    },
    promptSingleApproval(availability) {
      this.approvalDialog = {
        ids: availability?.id ? [availability.id] : [],
        availabilities: availability ? [availability] : [],
        label: availability?.service_name || "this availability",
        title: availability?.service_name || "Availability",
        description: this.formatAvailabilityWindow(availability || {}),
        slotDescription: "If you choose yes, the system will approve this service and immediately generate its slots.",
        step: 1,
      };
      this.approveAndCreateSlots = false;
      this.$bvModal.show("approve-availability-modal");
    },
    promptGroupApproval(group) {
      this.approvalDialog = {
        ids: group?.pending_ids || [],
        availabilities: (group?.availabilities || []).filter((item) => item.status === "pending_review"),
        label: group?.doctor_name || "this submitter",
        title: group?.doctor_name || "Submitter",
        description: `${group?.pending_count || 0} pending availability row(s) are ready for approval.`,
        slotDescription: "If you choose yes, the system will approve the pending rows and then create slots for every newly approved service under this submitter.",
        step: 1,
      };
      this.approveAndCreateSlots = false;
      this.$bvModal.show("approve-availability-modal");
    },
    promptGroupSlotCreation(group) {
      this.bulkSlotDialog = {
        label: group?.doctor_name || "this submitter",
        availabilities: group?.approved_availabilities || [],
      };
      this.$bvModal.show("bulk-create-slots-modal");
    },
    promptDelete(ids, label) {
      this.deleteDialog = {
        ids: ids || [],
        label: label || "the selected availability",
      };
      this.$bvModal.show("delete-availability-modal");
    },
    async approveSelected(createSlots) {
      if (!this.approvalDialog?.ids?.length) return;
      this.approvingSingle = true;
      this.approveAndCreateSlots = createSlots;
      try {
        const response = await axios.post("/approveappointmentavailabilities", {
          ids: this.approvalDialog.ids,
          create_slots: createSlots,
        });
        if (createSlots) {
          const createdDates = response.data?.created_dates || [];
          const previewDate = createdDates[0] || "";
          const createdRows = Number(response.data?.created_rows || 0);
          const createdSlots = Number(response.data?.created_slots || 0);
          const successMessage = createdRows
            ? `${createdSlots} appointment slots created across ${createdRows} service row(s) for ${this.approvalDialog.label}.`
            : (response.data?.message || `Availability approved for ${this.approvalDialog.label}.`);
          this.$toast.success(successMessage);
          this.showSkippedBatchWarnings(response.data?.skipped_services || [], this.approvalDialog.label);
          this.$bvModal.hide("approve-availability-modal");
          this.$bvModal.hide("availability-detail-modal");
          await this.initialLoad();
          if (previewDate) {
            this.activeScheduleTab = 1;
            await this.selectCalendarDate(previewDate);
          }
          return;
        }

        this.$toast.success(response.data?.message || "Availability approved successfully.");
        this.$bvModal.hide("approve-availability-modal");
        this.$bvModal.hide("availability-detail-modal");
        await this.initialLoad();
      } catch (error) {
        this.showRequestError(error, createSlots ? "Failed to approve availability and create slots." : "Failed to approve availability.");
      } finally {
        this.approvingSingle = false;
        this.approveAndCreateSlots = false;
      }
    },
    async handleBulkSlotCreation() {
      const previewDate = await this.createSlotsForList(this.bulkSlotDialog.availabilities, this.bulkSlotDialog.label);
      this.$bvModal.hide("bulk-create-slots-modal");
      await this.initialLoad();
      if (previewDate) {
        this.activeScheduleTab = 1;
        await this.selectCalendarDate(previewDate);
      }
    },
    async createSlotsForList(availabilities, label, suppressToast = false) {
      const items = (availabilities || []).filter((item) => item && item.id);
      if (!items.length) return "";

      this.bulkCreatingSlots = true;

      try {
        const response = items.length > 1
          ? await axios.post("/createappointmentslotsbatch", {
            availability_ids: items.map((item) => item.id),
          })
          : await axios.post("/createappointmentslots", {
            availability_id: items[0].id,
          });

        const createdRows = Number(response.data?.created_rows || (items.length === 1 ? 1 : 0));
        const createdSlots = Number(response.data?.created_slots || 0);
        const previewDate = response.data?.created_dates?.[0] || items[0]?.specific_date || items[0]?.start_date || "";
        const skippedServices = response.data?.skipped_services || [];

        if (!suppressToast) {
          if (createdRows) {
            const baseMessage = createdSlots
              ? `${createdSlots} appointment slots created across ${createdRows} service row(s) for ${label}.`
              : `Slots were created for ${createdRows} service row(s) under ${label}.`;
            this.$toast.success(baseMessage);
          }
          this.showSkippedBatchWarnings(skippedServices, label);
        }

        if (!createdRows && skippedServices.length && !suppressToast) {
          this.$toast.warning(`No slots were created for ${label}.`);
        }

        return previewDate;
      } catch (error) {
        this.showRequestError(error, "Failed to create appointment slots.");
        return "";
      } finally {
        this.bulkCreatingSlots = false;
      }
    },
    showSkippedBatchWarnings(skippedServices, label) {
      const items = (skippedServices || []).filter((item) => item && item.service_name);
      if (!items.length) return;

      const summary = items
        .slice(0, 3)
        .map((item) => item.reason ? `${item.service_name} (${item.reason})` : item.service_name)
        .join(", ");

      const suffix = items.length > 3 ? ` and ${items.length - 3} more` : "";
      this.$toast.warning(`Some services under ${label} were skipped: ${summary}${suffix}.`);
    },
    async deleteSelectedAvailabilities() {
      if (!this.deleteDialog.ids.length) return;
      this.deletingAvailability = true;
      try {
        const response = await axios.post("/deleteappointmentavailabilities", {
          ids: this.deleteDialog.ids,
        });
        this.$toast.success(response.data?.message || "Availability deleted successfully.");
        this.$bvModal.hide("delete-availability-modal");
        this.$bvModal.hide("availability-detail-modal");
        await this.initialLoad();
      } catch (error) {
        this.showRequestError(error, "Failed to delete availability.");
      } finally {
        this.deletingAvailability = false;
      }
    },
    async rejectSelected() {
      if (!this.rejectionReason.trim()) {
        this.$toast.error("Provide a reason before rejecting availability.");
        return;
      }
      this.rejecting = true;
      try {
        const response = await axios.post("/rejectappointmentavailabilities", {
          ids: this.rejectionIds,
          reason: this.rejectionReason,
        });
        this.$toast.success(response.data?.message || "Availability rejected successfully.");
        this.$bvModal.hide("reject-availability-modal");
        this.$bvModal.hide("availability-detail-modal");
        await this.initialLoad();
      } catch (error) {
        this.showRequestError(error, "Failed to reject availability.");
      } finally {
        this.rejecting = false;
      }
    },
    openSlotModal(availability) {
      this.slotAvailability = availability;
      this.$bvModal.show("create-slots-modal");
    },
    async saveSlots() {
      if (!this.autoSlotCount) {
        this.$toast.error("No valid slots can be created from this availability window.");
        return;
      }
      if (!this.slotGenerationDays) {
        this.$toast.error("No valid days were found in this approved availability range.");
        return;
      }
      this.savingSlots = true;
      try {
        const response = await axios.post("/createappointmentslots", {
          availability_id: this.slotAvailability.id,
        });
        const createdCount = Number(response.data?.created_slots || 0);
        this.$toast.success(createdCount ? `${createdCount} appointment slots created successfully.` : (response.data?.message || "Appointment slots created successfully."));
        this.$bvModal.hide("create-slots-modal");
        await this.initialLoad();
        const previewDate = response.data?.created_dates?.[0]
          || this.slotAvailability.specific_date
          || this.slotAvailability.start_date;
        if (previewDate) {
          this.activeScheduleTab = 1;
          await this.selectCalendarDate(previewDate);
        }
      } catch (error) {
        this.showRequestError(error, "Failed to create appointment slots.");
      } finally {
        this.savingSlots = false;
      }
    },
    async selectCalendarDate(date) {
      this.selectedCalendarDate = date;
      this.loadingDaySlots = true;
      try {
        const [slotResponse, bookingResponse] = await Promise.all([
          axios.get("/appointmentslotday", { params: { slot_date: date } }),
          axios.get("/appointmentbookingday", { params: { slot_date: date } }),
        ]);
        this.daySlots = slotResponse.data?.slots || [];
        this.daySlotBookings = bookingResponse.data?.bookings || [];
      } catch (error) {
        this.daySlotBookings = [];
        this.showRequestError(error, "Failed to load the selected day schedule.");
      } finally {
        this.loadingDaySlots = false;
      }
    },
    slotPopoverTarget(slot) {
      return `schedule-slot-card-${slot.id}`;
    },
    slotBookingDetails(slot) {
      const booking = this.daySlotBookingsBySlotId[slot.id]
        || slot.booking
        || slot.appointment_booking
        || slot.booked_patient
        || slot.patient
        || {};
      const patient = booking.patient || slot.patient || {};
      const rows = [
        {
          label: "Patient",
          value: slot.patient_name || booking.patient_name || booking.name || patient.name || patient.full_name || "",
        },
        {
          label: "Patient ID",
          value: slot.patient_id || booking.patient_id || patient.patient_id || patient.id || "",
        },
        {
          label: "Telephone",
          value: slot.telephone || slot.phone_number || booking.patient_contact || booking.telephone || booking.phone_number || patient.telephone || patient.phone_number || "",
        },
        {
          label: "Booking Ref",
          value: slot.booking_reference || slot.reference || booking.reference || booking.booking_reference || booking.public_id || "",
        },
        {
          label: "Booked On",
          value: slot.booked_at || booking.booked_at || booking.created_at || "",
        },
      ].filter((detail) => detail.value);

      return rows.map((detail) => ({
        ...detail,
        value: detail.label === "Booked On" ? this.formatDateTime(detail.value) : detail.value,
      }));
    },
    shiftMonth(step) {
      const next = new Date(this.currentMonth);
      next.setMonth(next.getMonth() + step);
      this.currentMonth = this.startOfMonth(next);
    },
    startOfMonth(date) {
      return new Date(date.getFullYear(), date.getMonth(), 1);
    },
    isoDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    formatDate(value) {
      if (!value) return "";
      return new Date(value).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
    formatDateTime(value) {
      if (!value) return "";
      return new Date(value).toLocaleString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    },
    formatTime(value) {
      if (!value) return "";
      const [hours, minutes] = String(value).split(":").map(Number);
      const date = new Date();
      date.setHours(hours || 0, minutes || 0, 0, 0);
      return date.toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit",
      });
    },
    shortTime(value) {
      return String(value || "").slice(0, 5);
    },
    addMinutes(timeValue, duration) {
      if (!timeValue) return "";
      const [hours, minutes] = timeValue.split(":").map(Number);
      const date = new Date();
      date.setHours(hours, minutes, 0, 0);
      date.setMinutes(date.getMinutes() + Number(duration || 0));
      return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
    },
    timeToMinutes(timeValue) {
      if (!timeValue) return 0;
      const [hours, minutes] = String(timeValue).split(":").map(Number);
      return (hours * 60) + minutes;
    },
    formatAvailabilityType(item) {
      return item.type === "recurring" ? `Recurring (${this.formatDayOfWeek(item.day_of_week)})` : "One-Time";
    },
    formatAvailabilityWindow(item) {
      const timeRange = `${this.formatTime(item.start_time)} - ${this.formatTime(item.end_time)}`;
      if (item.type === "recurring") {
        const range = `${this.formatDayOfWeek(item.day_of_week)}, ${this.formatDate(item.start_date)}${item.end_date ? ` to ${this.formatDate(item.end_date)}` : ""}`;
        return `${range} | ${timeRange}`;
      }
      return `${this.formatDate(item.specific_date)} | ${timeRange}`;
    },
    formatDayOfWeek(value) {
      if (!value) return "Recurring";
      return String(value).replace(/\b\w/g, (letter) => letter.toUpperCase());
    },
    formatStatus(value) {
      return String(value || "").replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
    },
    statusBadgeClass(status) {
      if (status === "approved") return "bg-soft-success text-success";
      if (status === "rejected") return "bg-soft-danger text-danger";
      if (status === "withdrawn") return "bg-soft-secondary text-secondary";
      return "bg-soft-warning text-warning";
    },
    money(value) {
      return Number(value || 0).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    showRequestError(error, fallbackMessage) {
      const message = error?.response?.data?.message || Object.values(error?.response?.data?.errors || {})?.[0]?.[0] || fallbackMessage;
      this.$toast.error(message);
    },
  },
};
</script>

<style scoped>
.schedule-center-tabs {
  gap: 0.75rem;
  border-bottom: 0;
}

.schedule-center-tabs .nav-item {
  margin-bottom: 0;
}

.schedule-center-tabs .nav-link {
  border: 1px solid #d9e3ee;
  border-radius: 999px;
  color: #52637a;
  background: #fff;
  padding: 0.7rem 1.15rem;
  font-weight: 600;
  transition: all 0.2s ease;
}

.schedule-center-tabs .nav-link.active {
  color: #fff;
  background: linear-gradient(135deg, #03967d 0%, #14b8a6 100%);
  border-color: transparent;
  box-shadow: 0 10px 26px rgba(3, 150, 125, 0.22);
}

.schedule-center-tabs .nav-link:hover:not(.active) {
  color: #0f172a;
  border-color: #b8c7d8;
}

.pending-pill {
  font-size: 0.85rem;
}

.service-filter {
  min-width: 180px;
}

.service-filter-select {
  min-width: 240px;
}

.schedule-review-card,
.calendar-shell-card {
  border-radius: 1rem;
}

.review-loading,
.review-empty {
  min-height: 220px;
  border: 1px dashed #d8e3ef;
  border-radius: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  background: #fbfdff;
  text-align: center;
  padding: 1rem;
}

.review-table-shell {
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
  overflow: hidden;
  background: #fff;
  max-height: 70vh;
  overflow-y: auto;
}

.review-table-shell::-webkit-scrollbar,
.review-group-details::-webkit-scrollbar,
:deep(.schedule-modal-shell .modal-body::-webkit-scrollbar) {
  width: 6px;
  height: 6px;
}

.review-table-shell::-webkit-scrollbar-track,
.review-group-details::-webkit-scrollbar-track,
:deep(.schedule-modal-shell .modal-body::-webkit-scrollbar-track) {
  background: transparent;
}

.review-table-shell::-webkit-scrollbar-thumb,
.review-group-details::-webkit-scrollbar-thumb,
:deep(.schedule-modal-shell .modal-body::-webkit-scrollbar-thumb) {
  background: rgba(100, 116, 139, 0.16);
  border-radius: 999px;
}

.review-table,
.review-detail-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.review-table thead th,
.review-detail-table thead th {
  padding: 0.95rem 1rem;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  background: #f8fbfc;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 2;
}

.review-table tbody td,
.review-detail-table tbody td {
  padding: 0.95rem 1rem;
  vertical-align: middle;
  border-bottom: 1px solid #edf2f7;
}

.review-table th + th,
.review-table td + td,
.review-detail-table th + th,
.review-detail-table td + td {
  border-left: 1px solid #edf2f7;
}

.review-group-row,
.review-detail-row {
  cursor: pointer;
  transition: background 0.22s ease, box-shadow 0.22s ease;
}

.review-group-row:hover,
.review-detail-row:hover {
  background: #fbfefd;
  box-shadow: inset 0 0 0 1px rgba(3, 150, 125, 0.08);
}

.review-group-row.open {
  background: #f4fbf9;
}

.review-submitter,
.review-service-cell {
  display: grid;
  gap: 0.2rem;
}

.review-submitter strong,
.review-service-cell strong {
  color: #0f172a;
}

.review-link-text {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  color: #0f766e;
  text-decoration: underline;
  text-decoration-thickness: 1.5px;
  text-underline-offset: 0.18em;
  text-decoration-color: rgba(15, 118, 110, 0.4);
  transition: color 0.18s ease, text-decoration-color 0.18s ease;
}

.review-group-row:hover .review-link-text,
.review-detail-row:hover .review-link-text {
  color: #047857;
  text-decoration-color: rgba(4, 120, 87, 0.75);
}

.review-submitter span,
.review-service-cell span {
  font-size: 0.84rem;
  color: #64748b;
}

.review-actions {
  display: flex;
  justify-content: flex-end;
  flex-wrap: nowrap;
  gap: 0.5rem;
  align-items: center;
}

.review-actions-inline {
  justify-content: flex-start;
  overflow-x: auto;
  padding-bottom: 0.15rem;
}

.icon-action-button {
  width: 2rem;
  height: 2rem;
  border: 1px solid #fecaca;
  border-radius: 999px;
  background: #fff5f5;
  color: #dc2626;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.18s ease;
}

.icon-action-button:hover {
  background: #fee2e2;
  border-color: #fca5a5;
}

.review-slide-row td {
  padding: 0;
  border-bottom: 1px solid #edf2f7;
}

.review-slide-cell {
  background: #fbfdff;
}

.review-group-details {
  max-height: 40vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0;
  background: #fbfdff;
}

.review-detail-table thead th {
  top: 0;
  z-index: 3;
}

.review-detail-table tbody td {
  background: #fff;
}

.review-expand-enter-active,
.review-expand-leave-active {
  transition: max-height 0.42s ease, opacity 0.32s ease, transform 0.34s ease;
}

.review-expand-enter,
.review-expand-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.review-pagination {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1rem;
  color: #52637a;
}

/* ─── General modal shell: header + footer pinned, body scrolls ─── */
:deep(.schedule-modal-shell .modal-dialog) {
  display: flex;
  align-items: center;
  min-height: calc(100vh - 4.5rem);
  margin-top: 2.25rem;
  margin-bottom: 2.25rem;
}

:deep(.schedule-modal-shell .modal-content) {
  max-height: calc(100vh - 4.5rem);
  display: flex;
  flex-direction: column;
  border-radius: 1rem;
  overflow: hidden;
}

:deep(.schedule-modal-shell .modal-header),
:deep(.schedule-modal-shell .modal-footer) {
  flex: 0 0 auto;
}

:deep(.schedule-modal-shell .modal-body) {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
}

/* ─── Availability detail modal overrides ─── */
:deep(.availability-detail-shell .modal-dialog) {
  max-width: 1160px;
  width: min(1160px, calc(100vw - 2rem));
  height: auto;
  max-height: calc(100vh - 2rem);
  margin: 1rem auto;
}

:deep(.availability-detail-shell) {
  overflow-y: hidden !important;
  overflow-x: hidden !important;
}

:deep(.availability-detail-shell .modal-content) {
  height: min(820px, calc(100vh - 2rem));
  max-height: min(820px, calc(100vh - 2rem));
}

:deep(.availability-detail-shell .modal-body) {
  padding: 1.5rem;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
}

:deep(.availability-detail-shell .modal-footer) {
  flex: 0 0 auto;
  padding: 1rem 1.5rem 1.5rem;
  border-top: 1px solid #edf2f7;
  background: #fff;
}

:deep(.availability-detail-shell .modal-body::-webkit-scrollbar) {
  width: 6px;
}

:deep(.availability-detail-shell .modal-body::-webkit-scrollbar-track) {
  background: transparent;
}

:deep(.availability-detail-shell .modal-body::-webkit-scrollbar-thumb) {
  background: rgba(100, 116, 139, 0.16);
  border-radius: 999px;
}

.availability-detail-modal {
  display: grid;
  gap: 1rem;
}

.availability-detail-hero {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.detail-eyebrow {
  display: inline-block;
  margin-bottom: 0.35rem;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #0f766e;
}

.availability-detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;
}

.detail-stat,
.approve-choice-card {
  border: 1px solid #dbe4f0;
  border-radius: 0.95rem;
  padding: 0.95rem 1rem;
  background: #fbfdff;
}

.detail-label {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  margin-bottom: 0.35rem;
}

.detail-value {
  color: #0f172a;
  font-weight: 600;
}

.detail-section {
  border-top: 1px solid #edf2f7;
  padding-top: 1rem;
}

.detail-section h6 {
  margin-bottom: 0.6rem;
}

.detail-copy {
  display: grid;
  gap: 0.45rem;
  color: #475569;
}

.detail-breaks {
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
}

.detail-break-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.45rem 0.7rem;
  border-radius: 999px;
  background: rgba(249, 115, 22, 0.1);
  color: #c2410c;
  font-size: 0.84rem;
  font-weight: 600;
}

.detail-note {
  padding: 0.85rem 1rem;
  border-radius: 0.9rem;
  background: #f8fafc;
  color: #334155;
}

.detail-note-danger {
  background: #fef2f2;
  color: #b91c1c;
}

.availability-detail-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  flex-wrap: wrap;
  width: 100%;
}

.calendar-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.calendar-shell {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 1rem;
  align-items: stretch;
}

.calendar-pane,
.slots-pane {
  min-height: 560px;
  max-height: 560px;
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
  background: #fff;
  padding: 1rem;
}

.slots-pane {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 0.55rem;
}

.calendar-head {
  font-size: 0.75rem;
  font-weight: 700;
  color: #64748b;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.calendar-cell {
  min-height: 78px;
  border: 1px solid #dbe4f0;
  border-radius: 0.85rem;
  background: #fff;
  padding: 0.55rem;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  position: relative;
}

.calendar-cell.touched {
  border-color: rgba(3, 150, 125, 0.35);
  background: rgba(3, 150, 125, 0.06);
}

.calendar-cell.booked {
  box-shadow: inset 0 0 0 1px rgba(220, 38, 38, 0.16);
}

.calendar-cell.active {
  border-color: #03967d;
  box-shadow: 0 0 0 1px rgba(3, 150, 125, 0.25);
}

.calendar-cell.today:not(.active) {
  border-color: rgba(3, 150, 125, 0.42);
  background: linear-gradient(180deg, rgba(240, 253, 250, 0.96), rgba(255, 255, 255, 0.98));
  box-shadow: inset 0 0 0 1px rgba(3, 150, 125, 0.12);
}

.calendar-cell.today .day-number {
  color: #047857;
}

.calendar-cell.muted {
  opacity: 0.45;
}

.day-number {
  font-weight: 700;
  color: #1e293b;
}

.calendar-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.75rem;
  padding: 0.15rem 0.45rem;
  border-radius: 999px;
  background: #03967d;
  color: #fff;
  font-size: 0.72rem;
}

.calendar-booked-dot {
  position: absolute;
  top: 0.7rem;
  right: 0.7rem;
  width: 0.55rem;
  height: 0.55rem;
  border-radius: 999px;
  background: #dc2626;
  box-shadow: 0 0 0 3px rgba(254, 226, 226, 0.95);
}

.slots-pane-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.9rem;
}

.slots-state {
  min-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #0f766e;
  border: 1px dashed #dbe4f0;
  border-radius: 0.9rem;
  padding: 1rem;
  background: #fbfdff;
}

.slots-state.empty {
  color: #64748b;
}

.slot-list {
  display: grid;
  gap: 0.75rem;
  overflow-y: auto;
  min-height: 0;
  padding-right: 0.25rem;
}

.slot-list::-webkit-scrollbar {
  width: 6px;
}

.slot-list::-webkit-scrollbar-track {
  background: transparent;
}

.slot-list::-webkit-scrollbar-thumb {
  background: rgba(100, 116, 139, 0.16);
  border-radius: 999px;
}

.slot-card {
  border: 1px solid #e2e8f0;
  border-radius: 0.85rem;
  padding: 0.85rem;
  background: #fff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.slot-card--booked {
  border-color: rgba(220, 38, 38, 0.18);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(254, 242, 242, 0.65));
}

.slot-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 16px 34px rgba(15, 23, 42, 0.06);
}

.slot-card-foot {
  display: flex;
  align-items: center;
}

.slot-booking-pill {
  display: inline-flex;
  align-items: center;
  min-height: 1.85rem;
  padding: 0.2rem 0.65rem;
  border-radius: 999px;
  background: rgba(220, 38, 38, 0.08);
  color: #b91c1c;
  font-size: 0.76rem;
  font-weight: 700;
}

:deep(.slot-booking-popover .popover) {
  border: 1px solid rgba(16, 185, 129, 0.3);
  border-radius: 1rem;
  box-shadow:
    0 24px 54px rgba(6, 95, 70, 0.18),
    0 0 0 1px rgba(255, 255, 255, 0.88);
  max-width: 380px;
  background: #ffffff;
  overflow: hidden;
}

:deep(.slot-booking-popover.bs-popover-auto[x-placement^="top"] .arrow::before),
:deep(.slot-booking-popover.bs-popover-top .arrow::before) {
  border-top-color: rgba(16, 185, 129, 0.3);
}

:deep(.slot-booking-popover.bs-popover-auto[x-placement^="bottom"] .arrow::before),
:deep(.slot-booking-popover.bs-popover-bottom .arrow::before) {
  border-bottom-color: rgba(16, 185, 129, 0.3);
}

:deep(.slot-booking-popover.bs-popover-auto[x-placement^="left"] .arrow::before),
:deep(.slot-booking-popover.bs-popover-left .arrow::before) {
  border-left-color: rgba(16, 185, 129, 0.3);
}

:deep(.slot-booking-popover.bs-popover-auto[x-placement^="right"] .arrow::before),
:deep(.slot-booking-popover.bs-popover-right .arrow::before) {
  border-right-color: rgba(16, 185, 129, 0.3);
}

:deep(.slot-booking-popover .popover-body) {
  padding: 0;
}

.slot-booking-popover-body {
  padding: 1rem 1.05rem 1.05rem;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 1), rgba(248, 250, 252, 0.98)),
    radial-gradient(circle at top right, rgba(16, 185, 129, 0.14), transparent 44%);
  border-top: 4px solid rgba(5, 150, 105, 0.72);
}

.slot-booking-popover-head {
  display: grid;
  gap: 0.15rem;
  margin-bottom: 0.6rem;
}

.slot-booking-popover-kicker {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #047857;
}

.slot-booking-popover-service {
  margin-bottom: 0.75rem;
  color: #475569;
  font-size: 0.85rem;
}

.slot-booking-popover-grid {
  display: grid;
  gap: 0.55rem;
}

.slot-booking-popover-row {
  display: grid;
  gap: 0.15rem;
  padding-bottom: 0.55rem;
  border-bottom: 1px solid #eef2f7;
}

.slot-booking-popover-row:last-child {
  padding-bottom: 0;
  border-bottom: 0;
}

.slot-booking-popover-label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #64748b;
}

.slot-booking-popover-value {
  color: #0f172a;
  font-size: 0.9rem;
  line-height: 1.45;
}

.slot-booking-popover-empty {
  color: #64748b;
  font-size: 0.85rem;
  line-height: 1.5;
}

.slot-breaks {
  min-height: 38px;
  border: 1px solid #ced4da;
  border-radius: 0.375rem;
  padding: 0.5rem 0.75rem;
}

.auto-slot-summary {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.85rem;
  margin-top: 0.25rem;
}

.summary-item {
  border: 1px solid #dbe4f0;
  border-radius: 0.85rem;
  padding: 0.9rem 1rem;
  background: #f8fbfc;
}

.summary-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 0.35rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.summary-value {
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
}

@media (max-width: 1199.98px) {
  .calendar-shell {
    grid-template-columns: 1fr;
  }

  .calendar-pane,
  .slots-pane {
    min-height: auto;
    max-height: none;
    padding: 0.9rem;
  }
}

@media (max-width: 767.98px) {
  .schedule-center-tabs {
    gap: 0.45rem;
  }

  .schedule-center-tabs .nav-link {
    padding: 0.65rem 0.95rem;
  }

  .review-table-shell {
    overflow-x: auto;
  }

  .availability-detail-grid {
    grid-template-columns: 1fr;
  }

  .review-actions {
    justify-content: flex-start;
  }

  .review-table,
  .review-detail-table {
    min-width: 860px;
  }

  .service-filter {
    min-width: 100%;
  }

  .service-filter-select {
    min-width: 100%;
  }

  .calendar-grid {
    gap: 0.4rem;
  }

  .calendar-cell {
    min-height: 72px;
  }

  .auto-slot-summary {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
